/**
 * Client-side summary calculators for `DataGridFooter`.
 *
 * These are used as a fallback when the backend does not return a `summary`
 * array for a given response (e.g. no pagination, or the caller doesn't wire
 * `serverSummary` through). Each function mirrors the corresponding branch of
 * legacy `calculateCustomSummary` / `CalculateSummary` in `grid.js`, including
 * its exact text formatting, so that switching between server and client paths
 * produces identical output.
 *
 * Pure TypeScript — no React imports. Safe to unit-test in isolation.
 */

import type { Row } from '@tanstack/react-table';
import {
  SUMMARY_TYPE_SUM,
  SUMMARY_TYPE_COUNT,
  SUMMARY_TYPE_MIN,
  SUMMARY_TYPE_MAX,
  SUMMARY_TYPE_AVG,
  SUMMARY_TYPE_TIMESHEET,
  SUMMARY_TYPE_STATUS,
  SUMMARY_TYPE_DURATION,
  SUMMARY_TYPE_HOURS_DECIMAL,
  STATUS_ID_LABEL_MAP,
  CURRENCY_SYMBOLS,
  SECONDS_PER_MINUTE,
  MINUTES_PER_HOUR,
  SECONDS_PER_HOUR,
  SECONDS_ROUNDING_FACTOR,
  FULL_HOUR_IN_MINUTES,
  STATUS_MIN_START,
  THOUSANDS_GROUP_SIZE,
  FIXED_DECIMAL_PLACES,
} from './summaryConstants';

// ---------------------------------------------------------------------------
// Row field helpers
// ---------------------------------------------------------------------------

/** A data row shaped like the backend's `{ Id, DeletedAt, Fields: [...] }` entity. */
type EntityFieldsRow = {
  Fields?: Array<{ Name?: string; DataType?: string; StringValue?: string }>;
} & Record<string, unknown>;

/**
 * Reads a field value from a grid row.
 *
 * Grid rows may come in two forms:
 *  - **Flattened** (`row[fieldName]` already set by legacy `SetupRows`) — checked first.
 *  - **Raw entity** (`row.Fields[]`) — fallback.
 *
 * Returns `undefined` if the field is not found in either form.
 */
export const getFieldValue = (original: unknown, fieldName: string): unknown => {
  if (!original || typeof original !== 'object') return undefined;

  const row = original as EntityFieldsRow;

  if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
    return row[fieldName];
  }

  return row.Fields?.find((f) => f.Name === fieldName)?.StringValue;
};

// ---------------------------------------------------------------------------
// Number parsing
// ---------------------------------------------------------------------------

/**
 * Parses a cell value into a finite number for summation purposes.
 * Strips non-numeric characters (thousands separators, currency symbols, etc.).
 *
 * Ported from `parseSummaryCellValue` in `columnUtils.ts` — kept here so all
 * summary math lives in one place. Re-exported from `columnUtils` for
 * backwards compatibility.
 */
export const parseSummaryCellValue = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value).replace(/[^\d,.-]/g, '');
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  // "1,234" — comma is likely a thousands separator.
  const commaLooksLikeThousands =
    lastDot === -1 && lastComma !== -1 && cleaned.length - lastComma - 1 === THOUSANDS_GROUP_SIZE;

  const normalized = commaLooksLikeThousands
    ? cleaned.replace(/,/g, '')
    : lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');

  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : 0;
};

// ---------------------------------------------------------------------------
// Formatting helpers (shared between calculators and widgets)
// ---------------------------------------------------------------------------

/** Formats a decimal number to 2 fixed decimal places with locale thousands separators. */
export const formatFixed2 = (n: number): string =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: FIXED_DECIMAL_PLACES,
    maximumFractionDigits: FIXED_DECIMAL_PLACES,
  });

/**
 * Converts a total number of seconds to an "HH:MM" string.
 * Mirrors `formatSecondsToTimeSpan` in legacy `grid.js`.
 */
export const formatSecondsToTimeSpan = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const remainingSeconds = totalSeconds - hours * SECONDS_PER_HOUR;
  const minutes = Math.floor(remainingSeconds / SECONDS_PER_MINUTE);
  return [hours.toString().padStart(2, '0'), minutes.toString().padStart(2, '0')].join(':');
};

/**
 * Converts decimal hours to an "H:MM" string.
 * Mirrors `formatHours` in legacy `grid.js`.
 */
export const formatHoursDecimal = (decimalHours: unknown): string => {
  const parsed = Number(decimalHours);
  const safe = Number.isFinite(parsed) ? parsed : 0;
  let hours = Math.floor(safe);
  let minutes = Math.round((safe - hours) * MINUTES_PER_HOUR);
  if (minutes === FULL_HOUR_IN_MINUTES) {
    hours += 1;
    minutes = 0;
  }
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Converts an "H:MM" or "H:MM:SS" time string to a human-readable
 * "X hrs YY min" label. Mirrors `formatTimeStringToText` in legacy `grid.js`.
 */
export const formatTimeStringToText = (timeString: string): string => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':').map(Number);
  const formattedHours = hours >= 0 ? `${hours} hrs` : '';
  const formattedMinutes = minutes >= 0 ? `${minutes.toString().padStart(2, '0')} min` : '';
  return [formattedHours, formattedMinutes].filter(Boolean).join(' ');
};

// ---------------------------------------------------------------------------
// Currency helpers
// ---------------------------------------------------------------------------

export type AmountStat = {
  Currency?: string;
  CurrencySymbol?: string;
  Amount: number;
  TotalTime?: number;
  RateWasChanged?: boolean;
  RatePerHour?: number;
};

/**
 * Returns the display symbol for a currency.
 * Prefers an explicit `CurrencySymbol` field; falls back to the `CURRENCY_SYMBOLS`
 * lookup; finally falls back to the raw currency code.
 */
export const getCurrencySymbol = (item: AmountStat): string =>
  item.CurrencySymbol ??
  CURRENCY_SYMBOLS[(item.Currency ?? '').toUpperCase()] ??
  item.Currency ??
  '';

/**
 * Parses an `AmountStats` JSON string from a row field into a typed array.
 * Returns an empty array for missing, null, non-string, or malformed values.
 */
export const parseAmountStats = (raw: unknown): AmountStat[] => {
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AmountStat[]) : [];
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// Time-span parsing (shared between duration and hours-decimal calculators)
// ---------------------------------------------------------------------------

/**
 * Converts a `TimeSpan` string ("HH:MM:SS[.mmm]") or a decimal-hours string
 * to a total number of seconds.
 * Mirrors `parseTimeSpanToSeconds` in legacy `grid.js`.
 */
export const parseTimeSpanToSeconds = (timeSpan: unknown): number => {
  if (!timeSpan) return 0;
  const str = String(timeSpan).trim();

  if (!str.includes(':')) {
    const decimal = parseFloat(str);
    return Number.isFinite(decimal) ? decimal * SECONDS_PER_HOUR : 0;
  }

  const noMillis = str.split('.')[0];
  const parts = noMillis.split(':').map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = parts;

  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return 0;
  }

  return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
};

// ---------------------------------------------------------------------------
// Individual calculators
// ---------------------------------------------------------------------------

/**
 * Standard DevExtreme-style aggregation (sum / min / max / avg / count).
 * Legacy `customizeText` always prefixes "Sum:" regardless of the actual
 * `summaryType` — we preserve that behaviour exactly.
 */
export const calculateGenericSummary = <T>(
  rows: Row<T>[],
  colId: string,
  summaryType: string
): string => {
  const values = rows.map((row) => parseSummaryCellValue(row.getValue(colId)));

  let value: number;
  switch (summaryType) {
    case SUMMARY_TYPE_MIN:
      value = values.length ? values.reduce((min, v) => (v < min ? v : min), values[0]) : 0;
      break;
    case SUMMARY_TYPE_MAX:
      value = values.length ? values.reduce((max, v) => (v > max ? v : max), values[0]) : 0;
      break;
    case SUMMARY_TYPE_AVG:
      value = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      break;
    case SUMMARY_TYPE_COUNT:
      value = values.length;
      break;
    case SUMMARY_TYPE_SUM:
    default:
      value = values.reduce((a, b) => a + b, 0);
      break;
  }

  return `Sum: ${formatFixed2(value)}`;
};

type TimesheetRow = {
  TaskName?: string;
  ProjectName?: string;
  Hours?: string | number;
};

/**
 * Custom timesheet aggregation.
 *
 * Mirrors `customTimesheetSummaryCalculation` in legacy `grid.js`.
 * Rows with `TaskName === "Total"` carry pre-aggregated subtotals.
 * When both a blank-ProjectName total (overall) and non-blank-ProjectName
 * totals (per-project) exist simultaneously, the sum is halved to cancel
 * the double-count — this is intentional legacy behaviour.
 */
export const calculateTimesheetSummary = <T>(rows: Row<T>[]): string => {
  let totalValue = 0;
  let totalValueByRecords = 0;
  let hasBlankProjectName = false;
  let hasNonBlankProjectName = false;

  for (const row of rows) {
    const original = row.original as TimesheetRow;
    const hours = Number(original.Hours) || 0;

    if (original.TaskName === 'Total') {
      const projectName = original.ProjectName ?? '';
      if (!hasBlankProjectName && projectName === '') hasBlankProjectName = true;
      if (!hasNonBlankProjectName && projectName !== '') hasNonBlankProjectName = true;
      totalValue += hours;
    } else {
      totalValueByRecords += hours;
    }
  }

  if (hasBlankProjectName && hasNonBlankProjectName) {
    totalValue /= 2.0;
  }
  const finalValue = totalValue === 0 ? totalValueByRecords : totalValue;
  return `Total: ${formatFixed2(finalValue)}`;
};

/**
 * Status aggregation: picks the minimum `StatusId` and maps it to a label.
 *
 * Mirrors `customStatusSummaryCalculation` in legacy `grid.js`.
 * No "Sum:" prefix — intentional legacy behaviour.
 */
export const calculateStatusSummary = <T>(rows: Row<T>[]): string => {
  let minStatus = STATUS_MIN_START;
  for (const row of rows) {
    const raw = getFieldValue(row.original, 'StatusId');
    const statusId = Number(raw);
    if (raw != null && !Number.isNaN(statusId)) {
      minStatus = Math.min(minStatus, statusId);
    }
  }
  return STATUS_ID_LABEL_MAP[minStatus] ?? '';
};

/**
 * Duration aggregation: sums `HH:MM:SS` (or decimal-hours) values and formats
 * the total as "Sum: HH:MM".
 *
 * Mirrors `customDurationTimeSummaryCalculation` in legacy `grid.js`.
 *
 * @param fieldName - The row property to read the duration from (e.g. `"Duration"`).
 */
export const calculateDurationSummary = <T>(rows: Row<T>[], fieldName: string): string => {
  let totalSeconds = 0;
  for (const row of rows) {
    const raw = getFieldValue(row.original, fieldName);
    if (raw != null) {
      totalSeconds =
        Math.round((totalSeconds + parseTimeSpanToSeconds(raw)) * SECONDS_ROUNDING_FACTOR) /
        SECONDS_ROUNDING_FACTOR;
    }
  }
  return `Sum: ${formatSecondsToTimeSpan(Math.round(totalSeconds))}`;
};

/**
 * Decimal-hours aggregation: sums decimal-hour values and formats the total
 * as "Sum: H:MM".
 *
 * Mirrors `customHoursSummaryCalculation` in legacy `grid.js`.
 *
 * @param fieldName - The row property to read the hours from (e.g. `"Hours"`).
 */
export const calculateHoursDecimalSummary = <T>(rows: Row<T>[], fieldName: string): string => {
  let total = 0;
  for (const row of rows) {
    const raw = getFieldValue(row.original, fieldName);
    if (raw != null) {
      total += Number(raw) || 0;
    }
  }
  let hours = Math.floor(total);
  let minutes = Math.round((total - hours) * MINUTES_PER_HOUR);
  if (minutes === FULL_HOUR_IN_MINUTES) {
    hours += 1;
    minutes = 0;
  }
  return `Sum: ${hours}:${minutes.toString().padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Selects and runs the correct client-side calculator for a single summary
 * column. Only invoked when no `serverSummary` entry is available for this
 * column.
 *
 * @param rows        - All pre-pagination rows from TanStack Table.
 * @param colId       - The column ID (matches the backend field name).
 * @param summaryType - Lowercase `totalSummaries` value from column metadata.
 */
export const calculateClientFallback = <T>(
  rows: Row<T>[],
  colId: string,
  summaryType: string
): string => {
  switch (summaryType) {
    case SUMMARY_TYPE_TIMESHEET:
      return calculateTimesheetSummary(rows);
    case SUMMARY_TYPE_STATUS:
      return calculateStatusSummary(rows);
    case SUMMARY_TYPE_DURATION:
      return calculateDurationSummary(rows, colId);
    case SUMMARY_TYPE_HOURS_DECIMAL:
      return calculateHoursDecimalSummary(rows, colId);
    case SUMMARY_TYPE_SUM:
    case SUMMARY_TYPE_MIN:
    case SUMMARY_TYPE_MAX:
    case SUMMARY_TYPE_AVG:
    case SUMMARY_TYPE_COUNT:
      return calculateGenericSummary(rows, colId, summaryType);
    default:
      return '';
  }
};
